// Branches data access. The ONLY place that talks to the `branches` table.
// Mirrors the hubs feature: pure row<->app mapping (unit-tested) + CRUD.

import { supabase } from "../../lib/supabase";
import type { Branch, BranchInput } from "../../lib/types";

/** A raw row as it comes back from the `branches` table. */
interface BranchRow {
  id: string;
  account_id: string;
  hub_id: string;
  name: string;
  code: string | null;
  city: string | null;
  state: string | null;
  lat: number | null;
  lng: number | null;
  capacity: number | null;
  share_pct: number | null;
  manager: string | null;
  active: boolean;
}

/** DB row -> app Branch. */
export function rowToBranch(row: BranchRow): Branch {
  return {
    id: row.id,
    hubId: row.hub_id,
    name: row.name,
    code: row.code ?? undefined,
    city: row.city ?? undefined,
    state: row.state ?? undefined,
    lat: row.lat,
    lng: row.lng,
    capacity: row.capacity,
    sharePct: row.share_pct == null ? null : Number(row.share_pct),
    manager: row.manager ?? undefined,
    active: row.active,
  };
}

/** App BranchInput -> DB row fields (account_id added by the caller's context). */
export function branchInputToRow(accountId: string, input: BranchInput) {
  return {
    account_id: accountId,
    hub_id: input.hubId,
    name: input.name.trim(),
    code: input.code?.trim() || null,
    city: input.city?.trim() || null,
    state: input.state?.trim() || null,
    lat: input.lat ?? null,
    lng: input.lng ?? null,
    capacity: input.capacity ?? null,
    share_pct: input.sharePct ?? null,
    manager: input.manager?.trim() || null,
    active: input.active,
  };
}

/** List all branches for an account, ordered by name. */
export async function listBranches(accountId: string): Promise<Branch[]> {
  const { data, error } = await supabase
    .from("branches")
    .select("*")
    .eq("account_id", accountId)
    .order("name");
  if (error) throw error;
  return (data as BranchRow[]).map(rowToBranch);
}

/** Create a new branch and return it. */
export async function createBranch(accountId: string, input: BranchInput): Promise<Branch> {
  const { data, error } = await supabase
    .from("branches")
    .insert(branchInputToRow(accountId, input))
    .select("*")
    .single();
  if (error) throw error;
  return rowToBranch(data as BranchRow);
}

/** Update an existing branch and return it. */
export async function updateBranch(
  accountId: string,
  id: string,
  input: BranchInput
): Promise<Branch> {
  const { data, error } = await supabase
    .from("branches")
    .update(branchInputToRow(accountId, input))
    .eq("id", id)
    .eq("account_id", accountId)
    .select("*")
    .single();
  if (error) throw error;
  return rowToBranch(data as BranchRow);
}

/**
 * Set a branch's active flag (our soft-delete / pause). Scoped by id AND
 * account_id. We never hard-delete branches from the UI.
 */
export async function setBranchActive(
  accountId: string,
  id: string,
  active: boolean
): Promise<void> {
  const { error } = await supabase
    .from("branches")
    .update({ active })
    .eq("id", id)
    .eq("account_id", accountId);
  if (error) throw error;
}
